import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiCurrencyDollarLight, PiPlusLight, PiPencilSimpleLineLight } from 'react-icons/pi';
import { useAppDispatch } from '../../../store';
import { fetchUserBillingRates, createUserBillingRate, updateUserBillingRate } from '../../../store/actions/economicsActions';
import { useOrgDefaultCurrency } from '../../../hooks/useOrgDefaultCurrency';
import { useDisplayCurrency } from '../../../hooks/useCurrency';
import { showModal } from '../../../store/actions/modalActions';
import { formatCurrency } from '../../../utils/formatCurrency';
import Button from '../../shared/TailwindComponents/Button';
import Table from '../../shared/TailwindComponents/Table';
import Skeleton from '../../shared/TailwindComponents/Skeleton';
import FormSection from '../../shared/FormComponents/FormSection';
import { FIELD_LABEL_CLASS } from '../../shared/TailwindComponents/FieldShell';
import clsx from 'clsx';
import { Well } from '../../shared/brand/Field';
import type { UserBillingRate } from '../../../types/economics';
import type { UserRatePayload } from './UserRateModal';

interface UserBillingRatePanelProps {
  userId: number | string;
  /** Firm admins may set/replace the rate; everyone else views only (US #2236). */
  canEdit?: boolean;
  /**
   * Drop the panel's own glass card and heading, leaving only the content.
   *
   * The settings "My rates" section already draws a titled card around this, and
   * a card inside a card is chrome, not grouping. Off by default so the admin
   * user-edit view, which renders the panel standalone, is unchanged.
   */
  bare?: boolean;
  /**
   * Controlled "the set/edit form is open".
   *
   * `bare` hands the section chrome to the caller, and the primary action
   * belongs in that chrome - every other personal settings section puts its
   * action in the section header, and a lone button floating over the top-right
   * of the body is what made this card read as mostly empty. Passing this pair
   * lets the header own the button while the form stays here. Omit both and the
   * panel keeps its own button and its own state, which is what the admin
   * user-edit view does.
   */
  formOpen?: boolean;
  onFormOpenChange?: (open: boolean) => void;
}

const todayStr = () => new Date().toISOString().split('T')[0];

/** The rate effective *today*: effective_from <= today and (no effective_to or effective_to >= today). */
const pickEffectiveToday = (rates: UserBillingRate[]): UserBillingRate | undefined => {
  const today = todayStr();
  return [...rates]
    .filter((r) => (r.effective_from || '') <= today && (!r.effective_to || r.effective_to >= today))
    .sort((a, b) => (b.effective_from || '').localeCompare(a.effective_from || ''))[0];
};

/**
 * Shows a user's active default billing rate + full rate history; firm admins
 * set and edit rates in the 'editUserRate' glass modal (US #2236, task #2258). Used on the user's
 * own profile (PersonalTab) and the admin user-edit view.
 */
const UserBillingRatePanel: React.FC<UserBillingRatePanelProps> = ({
  userId,
  canEdit = false,
  bare = false,
  formOpen,
  onFormOpenChange,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const preferredCurrency = useDisplayCurrency();
  const { defaultCurrency: orgDefaultCurrency } = useOrgDefaultCurrency();

  const [rates, setRates] = useState<UserBillingRate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [ownShowForm, setOwnShowForm] = useState(false);
  const showForm = formOpen ?? ownShowForm;
  const setShowForm = useCallback(
    (open: boolean) => {
      setOwnShowForm(open);
      onFormOpenChange?.(open);
    },
    [onFormOpenChange]
  );

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(false);
    try {
      const data = await dispatch(fetchUserBillingRates(userId));
      setRates(Array.isArray(data) ? data : []);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [dispatch, userId]);

  useEffect(() => {
    load();
  }, [load]);

  const currentRate = useMemo(() => pickEffectiveToday(rates), [rates]);

  /*
    Set / edit happens in a glass modal ('editUserRate'), not in a block that
    expanded inside the section and pushed the history down (feedback round 3).
  */
  const openRateModal = useCallback(
    (rate: UserBillingRate | null) => {
      dispatch(
        showModal({
          showModal: true,
          type: 'editUserRate',
          title: rate ? t('Edit billing rate') : t('Set new rate'),
          message: '',
          userRate: rate,
          defaultCurrency: preferredCurrency,
          orgDefaultCurrency: orgDefaultCurrency || undefined,
          onSave: async (payload: UserRatePayload) => {
            if (rate) await dispatch(updateUserBillingRate(userId, rate.id, payload));
            else await dispatch(createUserBillingRate(userId, payload));
          },
          onAfterSave: load,
        })
      );
    },
    [dispatch, t, preferredCurrency, orgDefaultCurrency, userId, load]
  );

  const openCreate = () => openRateModal(null);
  const openEdit = (r: UserBillingRate) => openRateModal(r);

  // Opened from outside (the section header's button): hand straight over to
  // the modal and drop the request, so the next click opens it again.
  useEffect(() => {
    if (!showForm) return;
    openRateModal(null);
    setShowForm(false);
  }, [showForm, openRateModal, setShowForm]);

  const columns = [t('Rate'), t('From'), t('To')];
  const rows = [...rates]
    .sort((a, b) => (b.effective_from || '').localeCompare(a.effective_from || ''))
    .map((r) => ({
      id: r.id,
      [t('Rate')]: formatCurrency(Number(r.default_rate), r.currency),
      [t('From')]: r.effective_from || '—',
      [t('To')]: r.effective_to || t('Current'),
      _raw: r,
    }));
  const historyActions = canEdit
    ? [{ label: t('Edit'), icon: <PiPencilSimpleLineLight />, onClick: (row: any) => openEdit(row._raw as UserBillingRate) }]
    : undefined;

  /**
   * Standalone, the panel wears the same card the settings sections wear —
   * `FormSection`, i.e. a `GlassCard` with the icon/title/description header
   * and a right-aligned action - rather than a hand-rolled `glass` div with its
   * own `<h4>`. `bare` drops it because the caller already drew one.
   */
  const body = (
    <div className={bare ? 'space-y-3' : 'space-y-4'}>

      {/* The rate itself: a labelled readout in a well, so the number is a
          stated value rather than large text floating in the card. */}
      {loading ? (
        <Skeleton variant="card" />
      ) : error ? (
        <Well className="flex items-center justify-between gap-3">
          <p className="text-sm text-fg-muted">{t('Could not load billing rates.')}</p>
          <Button variant="outline" size="small" onClick={load}>{t('Retry')}</Button>
        </Well>
      ) : currentRate ? (
        <Well className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className={clsx('w-full', FIELD_LABEL_CLASS)}>
            {t('Current effective rate today')}
          </span>
          <span className="text-xl font-medium text-fg-strong">
            {formatCurrency(Number(currentRate.default_rate), currentRate.currency)}
          </span>
          <span className="text-xs text-fg-muted">
            {(currentRate.currency || '').toUpperCase()} · {t('per hour')}
            {currentRate.effective_from ? ` · ${t('since')} ${currentRate.effective_from}` : ''}
          </span>
        </Well>
      ) : (
        <Well>
          <p className="text-sm text-fg-muted">{t('No active billing rate today.')}</p>
        </Well>
      )}

      {/* History - a labelled sub-block, sized to its rows rather than to a
          fixed 20rem box that stood half-empty for the usual one or two rates. */}
      {rates.length > 0 && (
        <div className="space-y-1.5">
          <p className={FIELD_LABEL_CLASS}>{t('Rate history')}</p>
          <Table
            columns={columns}
            data={rows}
            actions={historyActions}
            onRowClick={canEdit ? (row: any) => openEdit(row._raw as UserBillingRate) : undefined}
            rowSize="compact"
            min="min-h-0"
            max="max-h-[16rem]"
          />
        </div>
      )}
    </div>
  );

  if (bare) return body;

  return (
    <FormSection
      icon={<PiCurrencyDollarLight />}
      title={t('Default billing rate')}
      description={t('The default hourly rate used for time entries unless a matter sets an override.')}
      actions={
        canEdit ? (
          <Button variant="primary" size="small" icon={<PiPlusLight />} onClick={openCreate}>
            {t('Set new rate')}
          </Button>
        ) : undefined
      }
      noGrid
    >
      {body}
    </FormSection>
  );
};

export default UserBillingRatePanel;
